<script setup lang="ts">
import {computed, inject, provide, ref, useTemplateRef, watch} from 'vue';
import {
  apps,
  formatting,
  LegacyDelivery,
  LegacyOrder,
  legacyOrderCommands,
  legacyOrderKey,
  LegacyRma,
  LegacyRmaRow,
  legacyRmaRowKey,
  useContext,
  useLocalization
} from '@hantera/portal-app';
import {DecimalInput, DropdownButton, DropdownItem, Expandable, InlineInput} from '@hantera/design-system';
import Decimal from 'decimal.js';
import useBackend from '@/composition/useBackend';
import RmaExchange from './RmaExchange.vue';
import AppSlotView from '@/AppSlotView.vue';
import {Apps} from "@/composition/use-apps";
import {appsKey} from "@/keys";
import RmaRowAdjustment from "@/views/LegacyOrder/Rma/RmaRowAdjustment.vue";

const props = defineProps<{
  rma: LegacyRma
  row: LegacyRmaRow
}>();

const context = useContext();
const { currency, number } = formatting;
const backend = useBackend();
const order = inject(legacyOrderKey, ref<LegacyOrder>(<any>{}));
const delivery = order.value.deliveries.find(d => d.rows.findIndex(r => r.rowId === props.row.orderRow.rowId) >= 0) || <LegacyDelivery><any>{};
const originalOrderRow = delivery.rows.find(r => r.rowId === props.row.orderRow.rowId);
const appsContext = inject<Apps>(appsKey, {} as any);

const t = useLocalization();
const t2 = useLocalization('components.order.orderLine.details');

provide(legacyRmaRowKey, props.row);

const qtyInput = useTemplateRef<InstanceType<typeof InlineInput>>('qtyInput');
const inputQuantity = ref<Decimal>(new Decimal(0));
watch(() => props.row.quantity, (val) => {
  inputQuantity.value = props.row.quantity;
}, { immediate: true });

const decisions: {
  [code: string]: string
} = {
  reject: 'Reject',
  refund: 'Refund',
  exchange: 'Exchange',
}

const decision = ref(props.row.approved ? (props.row.exchange ? 'exchange' : 'refund') : 'reject');
const selectedDecisionLabel = computed(() => {
  return decisions[decision.value];
});

function setDecision(decisionCode: string) {
  const approve = decisionCode === 'exchange' || decisionCode === 'refund';
  if (approve != props.row.approved) {
    backend.legacyOrders.buildCommandGroup(
      order.value.orderId,
      (approve ? 'Approve' : 'Reject') + ' return of {articleNumber} in RMA {rmaNumber}',
      {
        articleNumber: props.row.orderRow.item.articleNumber,
        rmaNumber: props.rma.rmaNumber
      })
      .command(legacyOrderCommands.setRmaRowApproval, {
        rmaId: props.rma.rmaId,
        rmaRowId: props.row.rmaRowId,
        approved: approve,
      })
      .command(legacyOrderCommands.addActivityLog, {
        message: 'Changed approval of row {articleNumber} from RMA {rmaNumber} to {value}',
        fields: {
          articleNumber: props.row.orderRow.item.articleNumber,
          rmaNumber: props.rma.rmaNumber,
          value: approve ? 'approved' : 'not approved',
        }
      })
      .send();
  }
  else if (props.row.exchange && decisionCode != 'exchange') {
    backend.legacyOrders.buildCommandGroup(
      order.value.orderId,
      'Removed exchange of {articleNumber} to {exchangeArticleNumber} in RMA {rmaNumber}',
      {
        articleNumber: props.row.orderRow.item.articleNumber,
        exchangeArticleNumber: props.row.exchange.article.articleNumber,
        rmaNumber: props.rma.rmaNumber
      })
      .command(legacyOrderCommands.upsertRmaRowExchange, {
        rmaId: props.rma.rmaId,
        rmaRowId: props.row.rmaRowId,
        exchangeArticleNumber: null,
        exchangeQuantity: new Decimal(0),
      })
      .command(legacyOrderCommands.addActivityLog, {
        message: 'Removed exchange of {articleNumber} to {exchangeArticleNumber} in RMA {rmaNumber}',
        fields: {
          articleNumber: props.row.orderRow.item.articleNumber,
          exchangeArticleNumber: props.row.exchange.article.articleNumber,
          rmaNumber: props.rma.rmaNumber
        }
      })
      .send();
  }

  decision.value = decisionCode;
}

const imageUrl = computed(() => {
  return context.serverUrl() + '/legacy' + props.row.orderRow.item.image;
});

const canEditQuantity = computed(() => {
  return props.rma.state !== 'Completed';
});

const rowBasedAdjustments = computed(() => {
  return props.row.adjustments?.filter((a) => a.fields.some((f) => f.id === 'RowBasedAdjustment')) || [];
});

const returnAmountWithRowBasedAdjustments = computed(() => {
  const nonRowBasedAdjustments = props.row.adjustments.filter((a) => !rowBasedAdjustments.value.some((ra) => ra.actionId == a.actionId));
  const adjustmentTotal = nonRowBasedAdjustments.reduce((acc, a) => acc.add(a.adjustmentTotal), new Decimal(0));
  return props.row.refundRowTotal.add(adjustmentTotal);
});

const sellingUnitPrice = computed(() => {
  return currency(
    props.row.orderRow.quantity.gt(0) ? props.row.orderRow.rowTotal.dividedBy(props.row.orderRow.quantity) : new Decimal(0),
    order.value.currencyCode
  );
});

function qtyBlur() {
  try {
    
    const newValue = inputQuantity.value ?? new Decimal(0);
    if (!props.row.quantity.eq(newValue)) {

      backend.legacyOrders.buildCommandGroup(
        order.value.orderId,
        'RMA {rmaNumber} - {articleNumber} quantity from {oldQuantity} to {newQuantity}', {
        rmaNumber: props.rma.rmaNumber,
        oldQuantity: props.row.quantity,
        newQuantity: newValue,
        articleNumber: props.row.orderRow.item.articleNumber,
      }).command(legacyOrderCommands.updateRmaRowQuantity, {
        rmaId: props.rma.rmaId,
        rmaRowId: props.row.rmaRowId,
        quantity: newValue,
      })
        .send();
    }
  } catch {
    qtyInput.value?.setError();
    return;
  }
}

function toFullImageUrl(imageUrl: string) {
  return context.serverUrl() + '/legacy' + imageUrl;
}

const showRmaRowDetails = computed(() => {
  return appsContext.getComponentsFor(apps.legacySlots.legacyOrder.rma.rowDetails).length > 0;
});

const showOrderRowDetails = computed(() => {
  return appsContext.getComponentsFor(apps.legacySlots.legacyOrder.delivery.rowDetails).length > 0;
});

const unitPrice = computed(() => {
  return props.row.orderRow.unitPrice
})

const discount = computed(() => {
  const adjustmentTotal = props.row.adjustments.reduce((acc, a) => acc.add(a.adjustmentTotal), new Decimal(0)).mul(-1);
  return !adjustmentTotal || !props.row?.quantity ? 0 : adjustmentTotal.div(props.row?.quantity ?? 1);
})

const availableToReturn = computed(() => {
  
  if (props.rma.state === 'Completed') {
    return orderedQty.value.sub(props.row.orderRow.returnedQuantity).sub(props.row.orderRow.lostQuantity).add(props.row.quantity)
  }
  
  return orderedQty.value.sub(props.row.orderRow.returnedQuantity).sub(props.row.orderRow.lostQuantity)
  
})

const atrTitle = computed(() => {
  return `${t('orderRow.availableToReturnTooltip')}\nSales quantity: ${formatting.number(props.row.orderRow.quantity)}\nReturned: ${formatting.number(props.row.orderRow.returnedQuantity)}\nLost: ${formatting.number(props.row.orderRow.lostQuantity)}`
})

const atrWarning = computed(() => availableToReturn.value.lt(props.row.quantity))

const orderedQty = computed(() => {
  if (props.row.orderRow.fields['NotOriginallyOnTheOrder'] === 'True') {
    return new Decimal(0)
  }

  return props.row.quantity
})

</script>

<template>
  <div class="rma-row__panel box-content">
    <div class="rma-row p">
      <div class="rma-row__item">
        <div class="rma-row__product-image">
          <img :src="imageUrl" />
        </div>
        <div class="rma-row__identity">
          <span class="rma-row__item-number">{{ row.orderRow.item.articleNumber }}</span>
          <span class="rma-row__item-name" :title="row.orderRow.item.name">{{ row.orderRow.item.name }}</span>
        </div>
        <div class="rma-row__quantity">
          <DecimalInput
            v-if="canEditQuantity"
            v-model="inputQuantity"
            @blur="qtyBlur"
            class="qtyInput"
            ref="qtyInput"
          />
          <span v-else>
            {{ row.quantity }}
          </span>
          pcs ({{ row.received }} received)
        </div>
        <div class="rma-row__customer-reason" :title="row.reason">
          {{ row.reason }}
        </div>
        <span class="rma-row__delivery">{{ delivery.deliveryNumber }}</span>
      </div>

      <div
        class="rma-row__comment"
        v-if="row.inventoryComment"
      >
        {{ row.inventoryComment }}
      </div>
      
      
      <div
        class="rma-row__images"
        v-if="row.imageUrls && row.imageUrls.length > 0"
      >
        <a
          :href="toFullImageUrl(image)"
          target="_blank"
          v-for="image in row.imageUrls"
          :key="image"
        ><img
            :src="toFullImageUrl(image)"
            width="50"
            height="50"
            class="rma-row__image"
          /></a>

      </div>
      <div class="rma-row__summary">
        <div class="rma-row__decision">
          <DropdownButton
            :label="selectedDecisionLabel"
            :disabled="rma.state === 'Completed'"
          >
            <DropdownItem @click="setDecision('refund')">Refund</DropdownItem>
            <DropdownItem @click="setDecision('exchange')">Exchange</DropdownItem>
            <DropdownItem @click="setDecision('reject')">Reject</DropdownItem>
          </DropdownButton>
        </div>

        <div class="rma-row__refund-amount">
          <div class="rma-row__refund-amount-label">{{ t('orderRow.orderedQuantity') }}</div>
          {{ currency(orderedQty, order.currencyCode) }}
        </div>

        <div
          class="rma-row__refund-amount"
          :class="{
            'warning': atrWarning,
          }"
          :title="atrTitle"
        >
          <div class="rma-row__refund-amount-label">{{ t('orderRow.availableToReturn') }}</div>
          {{ currency(availableToReturn, order.currencyCode) }}
        </div>
        
        <div class="rma-row__refund-amount">
          <div class="rma-row__refund-amount-label">{{ t('orderRow.unitPrice') }}</div>
          {{ currency(unitPrice, order.currencyCode) }}
        </div>
        <div class="rma-row__refund-amount">
          <div class="rma-row__refund-amount-label">{{ t2('discount') }}</div>
          {{ currency(discount, order.currencyCode) }}
        </div>
        
        <div class="rma-row__refund-amount">
          <div class="rma-row__refund-amount-label">{{ t('orderRow.rowTotal') }}</div>
          <div class="rma-row__refund-amount-total">{{ currency(returnAmountWithRowBasedAdjustments, order.currencyCode) }}</div>
        </div>
      </div>

      <div
        class="rma-row__exchange"
        v-if="decision === 'exchange'"
      >
        <RmaExchange
          :rma="rma"
          :row="row"
        />
      </div>
    </div>

    <Expandable label="Details" class="details-expandable">
      <div class="details">
        <div class="extras">
          <div v-if="showRmaRowDetails" class="extras-section">
            <div class="extras-section-label">RMA line details</div>
            <AppSlotView
              :slot="apps.legacySlots.legacyOrder.rma.rowDetails"
              :context="{
                  rmaRow: row
                }"
            />
          </div>
          <div v-if="showOrderRowDetails" class="extras-section">
            <div class="extras-section-label">Order line details</div>
            <AppSlotView
              :slot="apps.legacySlots.legacyOrder.delivery.rowDetails"
              :context="{
                  delivery,
                  orderRow: row.orderRow
                }"
            />
          </div>
        </div>

        <div class="row-adjustments p">
          <div
            v-for="(adjustment, idx) in row.adjustments"
            :key="idx"
            class="row-adjustment"
          >
            <RmaRowAdjustment :order="order" :row="row" :adjustment="adjustment" />
          </div>
        </div>
      </div>
    </Expandable>

    <AppSlotView :slot="apps.legacySlots.legacyOrder.rma.row" />
  </div>
</template>

<style scoped>

:deep(.qtyInput) {
  display: inline-block;
  width: 50px;
}

.details {
  display: grid;
  grid-template-columns: 50% 50%;
}

.row-adjustments {
  display: grid;
  grid-template-columns: auto auto;
  grid-auto-rows: min-content;
  grid-auto-columns: auto;
  row-gap: 3px;
  column-gap: 10px;
}

.row-adjustment {
  display: contents;
}

.details-expandable :deep(.expandable__label) {
  padding-left: 5px;
  padding-right: 5px;
  border-top: 1px solid hsl(var(--color-70));
}

.extras-section {
  border-color: hsl(var(--color-70));
  border-width: 1px 1px 0 0;
  border-style: solid;
}

.extras-section:first-child {
  border-top-width: 0;
}

.extras-section-label {
  font-weight: 500;
  padding: 5px 5px 0 5px;
}

.warning {
  background-color: var(--color-warning);
  border-radius: 4px;
}

@container (max-width: 550px) {
  .rma-row__decision {
    grid-row: 2 / 3;
    grid-column: 1 / -1;
  }
  .rma-row__summary {
    grid-template-columns: repeat(5, max-content);
    padding-left: 0;
  }
  .rma-row__exchange {
    padding-left: 0;
  }
  .rma-row__customer-reason {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
}

@container (max-width: 769px) {
  .details {
    display: flex;
    flex-direction: column;
  }

  .extras {
    order: 1;
  }

  .row-adjustments {
    order: 0;
  }

  .extras-section {
    border-right-width: 0;
  }

  .extras-section:first-child {
    border-top-width: 1px;
  }
}

</style>

<style>
.rma-row__panel {
  margin: 3px 0;
}

.rma-row {
  display: grid;
  grid-auto-rows: auto;
  gap: 8px;
}

.rma-row__item {
  display: grid;
  grid-template-columns: calc(20px + 16px) auto auto auto;
  grid-auto-rows: min-content;
  column-gap: 5px;
  row-gap: 2px;
  white-space: nowrap;
  align-items: center;

  line-height: 16px;
}

.rma-row__product-image {
  position: relative;
  grid-column: 1;
  grid-row: 1 / 3;
}

.rma-row__product-image img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.rma-row__identity {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  grid-column: 2 / span 3;

  display: flex;
}

.rma-row__item-number {
  font-weight: 600;
}

.rma-row__item-name {
  padding-left: 5px;
  flex-grow: 1;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.rma-row__unit-price {
  text-align: right;
}

.rma-row__refund-amount {
  text-align: right;
  padding: 3px;
}

.rma-row__comment,
.rma-row__summary,
.rma-row__images,
.rma-row__app-details,
.rma-row__exchange {
  padding-left: 43px;
}

.rma-row__comment {
  font-style: italic;
}

.rma-row__summary {
  display: grid;
  grid-template-columns: auto max-content max-content max-content max-content max-content;
  grid-auto-rows: min-content;
  align-items: center;
  gap: 14px;
}

.rma-row__images {
  display: flex;
  flex-wrap: wrap;
}

.rma-row__image {
  object-fit: contain;
  width: 45px;
  height: 45px;
}

.rma-row__original-row {
  margin-top: 5px;
}

.rma-row__refund-amount-label {
  white-space: nowrap;
  font-size: 12px;
  text-align: right;
  padding-left: 10px;
  color: hsl(var(--color-text-50));
}

.rma-row__refund-amount-total {
  font-weight: 500;
}
</style>